import Badge from "@/components/core/badge";
import { Form } from "@/components/core/forms/form";
import { ProgressBar } from "@/components/core/progress-bar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { useAuth } from "@/hooks/use-auth";
import { useValidationError } from "@/hooks/use-validation-errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useCaseFilingEvidenceMutation } from "../case-filing/api/queries/case-filing-queries";
import type { CaseRecord } from "../case-record/types/case-records";
import { type CaseFilingAttachmentFormSchema, caseFilingAttachmentFormSchema } from "../dashboard/types/case-type";
import CaseFilingEvidenceList from "./components/case-filing-evidence-list";

export interface CaseFilingEvidenceRef {
	handleEvidenceSubmit: () => Promise<boolean>;
	handleResendFailed: () => Promise<boolean>;
}

const CaseFilingEvidence = forwardRef<
	CaseFilingEvidenceRef,
	{
		data?: CaseRecord;
	}
>(({ data }, ref) => {
	const { user } = useAuth();
	const occ = user?.is_occ;

	const form = useForm<CaseFilingAttachmentFormSchema>({
		resolver: zodResolver(caseFilingAttachmentFormSchema),
		mode: "onChange",
		defaultValues: {
			id: data?.id,
			files: data?.files || [],
			annexes: data?.annexes || [],
			payment: data?.payment || [],
			evidences: data?.evidence || [],
		},
	});

	const caseRecordEvidenceMutation = useCaseFilingEvidenceMutation();

	const [errors, _, getFirstError] = useValidationError();
	const [savingProgress, setSavingProgress] = useState<{
		current: number;
		total: number;
		message: string;
		currentItemId?: string;
		savedItems: Set<string>;
		failedItems: Set<string>;
	} | null>(null);

	// Permanent state to keep track of saved/failed items even after progress is cleared
	const [permanentItemStatus, setPermanentItemStatus] = useState<{
		savedItems: Set<string>;
		failedItems: Set<string>;
	}>({
		savedItems: new Set(),
		failedItems: new Set(),
	});

	// Refs to access EvidenceList form data
	const documentaryEvidenceRef = useRef<any>(null);
	const testimonialEvidenceRef = useRef<any>(null);
	const objectEvidenceRef = useRef<any>(null);

	// Update form when data changes
	useEffect(() => {
		if (data) {
			form.reset({
				id: data.id,
				files: data.files || [],
				annexes: data.annexes || [],
				payment: data.payment || [],
				evidences: data.evidences || [],
			});
		}
	}, [data]);

	// Store failed evidence items for retry
	const [failedEvidences, setFailedEvidences] = useState<any[]>([]);

	const handleEvidenceSubmit = async (): Promise<boolean> => {
		try {
			// Collect evidence data from all EvidenceList components
			const allEvidences = [];

			// Get Documentary evidence
			if (documentaryEvidenceRef.current) {
				const documentaryFiles = documentaryEvidenceRef.current.getValues?.("files") || [];
				// Add evidence_type to each documentary evidence
				const documentaryEvidencesWithType = documentaryFiles.map((file: any) => ({
					...file,
					evidence_type: "Documentary",
				}));
				allEvidences.push(...documentaryEvidencesWithType);
			}

			// Get Testimonial evidence
			if (testimonialEvidenceRef.current) {
				const testimonialFiles = testimonialEvidenceRef.current.getValues?.("files") || [];
				// Add evidence_type to each testimonial evidence
				const testimonialEvidencesWithType = testimonialFiles.map((file: any) => ({
					...file,
					evidence_type: "Testimonial",
				}));
				allEvidences.push(...testimonialEvidencesWithType);
			}

			// Get Object evidence (if not OCC)
			if (objectEvidenceRef.current && !occ) {
				const objectFiles = objectEvidenceRef.current.getValues?.("files") || [];
				// Add evidence_type to each object evidence
				const objectEvidencesWithType = objectFiles.map((file: any) => ({
					...file,
					evidence_type: "Object",
				}));
				allEvidences.push(...objectEvidencesWithType);
			}

			// If no evidence to submit, return success
			if (allEvidences.length === 0) {
				return true;
			}

			// Initialize progress
			const savedItems = new Set<string>();
			const failedItems = new Set<string>();
			const failedEvidencesList: any[] = [];

			setSavingProgress({
				current: 0,
				total: allEvidences.length,
				message: `Preparing to save ${allEvidences.length} evidence item${allEvidences.length > 1 ? "s" : ""}...`,
				savedItems,
				failedItems,
			});

			// Process evidence items individually
			let successCount = 0;
			const errors: any[] = [];

			for (let i = 0; i < allEvidences.length; i++) {
				const evidence = allEvidences[i];
				const itemId = evidence._id || evidence.id || `item-${i}`;

				// Get display name for the evidence item
				const evidenceName = evidence.exhibit || evidence.witness_name || evidence.description || `Item ${i + 1}`;

				// Update progress with current item being processed
				setSavingProgress({
					current: i,
					total: allEvidences.length,
					message: `Saving evidence ${i + 1} of ${allEvidences.length}: ${evidenceName}...`,
					currentItemId: itemId,
					savedItems,
					failedItems,
				});

				try {
					// Send individual evidence item to server
					const formData = {
						id: data?.id,
						evidences: [evidence], // Send one item at a time
					};

					const response = await caseRecordEvidenceMutation.mutateAsync(formData);
					if (response) {
						successCount++;
						savedItems.add(itemId);

						// Update progress after successful save
						setSavingProgress({
							current: i + 1,
							total: allEvidences.length,
							message: `Saved evidence ${i + 1} of ${allEvidences.length}: ${evidenceName}`,
							currentItemId: undefined, // Clear current item
							savedItems,
							failedItems,
						});
					}

					// Small delay between items to prevent overwhelming the server
					if (i < allEvidences.length - 1) {
						await new Promise((resolve) => setTimeout(resolve, 300));
					}
				} catch (error) {
					console.error(`Error submitting evidence item ${i + 1} (${evidenceName}):`, error);
					failedItems.add(itemId);
					failedEvidencesList.push(evidence); // Store failed evidence for retry

					// Add all remaining items as failed (since we're stopping here)
					for (let j = i + 1; j < allEvidences.length; j++) {
						const remainingEvidence = allEvidences[j];
						const remainingItemId = remainingEvidence._id || remainingEvidence.id || `item-${j}`;
						failedItems.add(remainingItemId);
						failedEvidencesList.push(remainingEvidence);
					}

					errors.push({
						index: i + 1,
						name: evidenceName,
						error,
					});

					// Update progress to show failed item
					setSavingProgress({
						current: i + 1,
						total: allEvidences.length,
						message: `Failed to save: ${evidenceName} - Process stopped`,
						currentItemId: undefined,
						savedItems,
						failedItems,
					});

					// Stop processing on first failure
					break;
				}
			}

			// Store failed evidences for potential retry
			setFailedEvidences(failedEvidencesList);

			// Update permanent status and clear progress after delay
			setPermanentItemStatus({
				savedItems: new Set([...permanentItemStatus.savedItems, ...savedItems]),
				failedItems: new Set([...permanentItemStatus.failedItems, ...failedItems]),
			});

			setTimeout(() => {
				setSavingProgress(null);
			}, 3000);

			// Return success only if ALL evidence was saved successfully
			if (successCount === allEvidences.length) {
				return true;
			}

			// If any evidence failed, log details and return false
			if (errors.length > 0) {
				console.warn(`Process stopped after failure. Saved ${successCount} of ${allEvidences.length} evidence items. Failed items:`, errors);
			}

			return false;
		} catch (error) {
			console.error("Error submitting evidence:", error);
			setSavingProgress(null);
			return false;
		}
	};

	const handleResendFailed = async (): Promise<boolean> => {
		try {
			// If no failed evidences, return success
			if (failedEvidences.length === 0) {
				return true;
			}

			// Initialize progress for retry
			const savedItems = new Set<string>();
			const failedItems = new Set<string>();
			const newFailedEvidences: any[] = [];

			setSavingProgress({
				current: 0,
				total: failedEvidences.length,
				message: `Retrying ${failedEvidences.length} failed evidence item${failedEvidences.length > 1 ? "s" : ""}...`,
				savedItems,
				failedItems,
			});

			// Process failed evidence items individually
			let successCount = 0;
			const errors: any[] = [];

			for (let i = 0; i < failedEvidences.length; i++) {
				const evidence = failedEvidences[i];
				const itemId = evidence._id || evidence.id || `item-${i}`;

				// Get display name for the evidence item
				const evidenceName = evidence.exhibit || evidence.witness_name || evidence.description || `Item ${i + 1}`;

				// Update progress with current item being processed
				setSavingProgress({
					current: i,
					total: failedEvidences.length,
					message: `Retrying evidence ${i + 1} of ${failedEvidences.length}: ${evidenceName}...`,
					currentItemId: itemId,
					savedItems,
					failedItems,
				});

				try {
					// Send individual evidence item to server
					const formData = {
						id: data?.id,
						evidences: [evidence], // Send one item at a time
					};

					const response = await caseRecordEvidenceMutation.mutateAsync(formData);
					if (response) {
						successCount++;
						savedItems.add(itemId);

						// Update progress after successful save
						setSavingProgress({
							current: i + 1,
							total: failedEvidences.length,
							message: `Saved evidence ${i + 1} of ${failedEvidences.length}: ${evidenceName}`,
							currentItemId: undefined, // Clear current item
							savedItems,
							failedItems,
						});
					}

					// Small delay between items to prevent overwhelming the server
					if (i < failedEvidences.length - 1) {
						await new Promise((resolve) => setTimeout(resolve, 300));
					}
				} catch (error) {
					console.error(`Error retrying evidence item ${i + 1} (${evidenceName}):`, error);
					failedItems.add(itemId);
					newFailedEvidences.push(evidence); // Still failed, keep for next retry
					errors.push({
						index: i + 1,
						name: evidenceName,
						error,
					});

					// Update progress to show failed item
					setSavingProgress({
						current: i + 1,
						total: failedEvidences.length,
						message: `Failed again: ${evidenceName}`,
						currentItemId: undefined,
						savedItems,
						failedItems,
					});

					// Continue with next item even if one fails
				}
			}

			// Update failed evidences list with items that still failed
			setFailedEvidences(newFailedEvidences);

			// Update permanent status and clear progress after delay
			setPermanentItemStatus({
				savedItems: new Set([...permanentItemStatus.savedItems, ...savedItems]),
				failedItems: new Set([...permanentItemStatus.failedItems, ...failedItems]),
			});

			setTimeout(() => {
				setSavingProgress(null);
			}, 3000);

			// Return success if at least some evidence was saved
			if (successCount > 0) {
				if (errors.length > 0) {
					console.warn(`Saved ${successCount} of ${failedEvidences.length} retried items. Still failed:`, errors);
				}
				return true;
			}

			return false;
		} catch (error) {
			console.error("Error retrying failed evidences:", error);
			setSavingProgress(null);
			return false;
		}
	};

	useImperativeHandle(ref, () => ({
		handleEvidenceSubmit,
		handleResendFailed,
	}));

	return (
		<div className="flex flex-col w-full max-w-base p-6 gap-6">
			{savingProgress && (
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-blue-900">
							Saving Evidence: {savingProgress.current} / {savingProgress.total}
						</span>
						<span className="text-xs text-blue-700">{Math.round((savingProgress.current / savingProgress.total) * 100)}%</span>
					</div>
					<ProgressBar
						value={savingProgress.current}
						max={savingProgress.total}
						label={savingProgress.message}
						size="md"
						color={savingProgress.failedItems.size > 0 ? "danger" : "primary"}
					/>
				</div>
			)}
			{!savingProgress && failedEvidences.length > 0 && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
					<div className="flex items-center justify-between">
						<div className="flex flex-col">
							<span className="text-sm font-medium text-red-900">
								{failedEvidences.length} evidence item{failedEvidences.length > 1 ? "s" : ""} failed to save
							</span>
							<span className="text-xs text-red-700">Click retry to attempt saving failed items again</span>
						</div>
						<button
							type="button"
							onClick={handleResendFailed}
							className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors duration-200"
						>
							Retry Failed
						</button>
					</div>
				</div>
			)}
			<div className="flex flex-col w-full gap-3">
				<Form {...form}>
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Pieces of Evidence</span>
							<span className="text-base text-foreground/70">
								Upload the Documentary Exhibits, Judicial Affidavits, and Other Pieces of Evidence. Ensure all files are clear and legible.
							</span>
						</div>
						<div className="flex flex-col w-full rounded-md border border-border/50">
							<div className="flex flex-col w-full mt-2">
								<Tabs variant="compact" defaultValue="documents" orientation="horizontal">
									<TabsList>
										<TabsTrigger value="documents">Documents</TabsTrigger>
										<TabsTrigger value="testimonial">Testimonial</TabsTrigger>
										<TabsTrigger value="object">Object</TabsTrigger>
									</TabsList>
									<TabsContent value="documents">
										<div className="flex flex-col w-full p-4">
											<CaseFilingEvidenceList
												ref={documentaryEvidenceRef}
												metadata={{
													evidence_type: "Documentary",
												}}
												data={data?.evidence?.filter((e) => e.evidence_type === "Documentary") || []}
												savingProgress={savingProgress}
												permanentItemStatus={permanentItemStatus}
												schema={[
													{
														className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
														name: "exhibit",
														label: "Exhibit",
														type: "text_input",
														placeholder: "Enter exhibit",
													},
													{
														name: "description",
														label: "Description",
														type: "textarea",
														placeholder: "Enter description",
													},
													{
														name: "purpose",
														label: "Purpose",
														type: "textarea",
														placeholder: "Enter purpose",
													},
												]}
											/>
										</div>
									</TabsContent>
									<TabsContent value="testimonial">
										<div className="flex flex-col w-full p-4">
											<CaseFilingEvidenceList
												ref={testimonialEvidenceRef}
												metadata={{
													evidence_type: "Testimonial",
												}}
												data={data?.evidence?.filter((e) => e.evidence_type === "Testimonial") || []}
												savingProgress={savingProgress}
												permanentItemStatus={permanentItemStatus}
												schema={[
													{
														name: "witness_name",
														label: "Witness Name",
														type: "text_input",
														placeholder: "Enter witness name",
													},
													{
														name: "description",
														label: "Description",
														type: "textarea",
														placeholder: "Enter description",
													},
												]}
											/>
										</div>
									</TabsContent>
									<TabsContent value="object">
										{!occ && (
											<div className="flex flex-col w-full p-4">
												<CaseFilingEvidenceList
													ref={objectEvidenceRef}
													metadata={{
														evidence_type: "Object",
													}}
													data={data?.evidence?.filter((e) => e.evidence_type === "Object") || []}
													savingProgress={savingProgress}
													permanentItemStatus={permanentItemStatus}
													schema={[
														{
															className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
															name: "exhibit",
															label: "Exhibit",
															type: "text_input",
															placeholder: "Enter exhibit",
														},
														{
															name: "description",
															label: "Description",
															type: "textarea",
															placeholder: "Enter description",
														},
														{
															name: "purpose",
															label: "Purpose",
															type: "textarea",
															placeholder: "Enter purpose",
														},
													]}
												/>
											</div>
										)}
									</TabsContent>
								</Tabs>
							</div>
						</div>
					</div>
				</Form>
			</div>
		</div>
	);
});

export default CaseFilingEvidence;
